import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import DataTable from 'components/Common/DataTable';
import { ColumnDef } from '@tanstack/react-table';
import Switch from 'components/Common/Switch';
import TimePicker from 'components/Common/TimePicker';
import { useFieldArray } from 'react-hook-form';
import { RiDeleteBin6Line } from 'react-icons/ri';
import { useMemo } from 'react';
import { MdOutlineContentCopy } from 'react-icons/md';

export default function WorkingHoursSection({
  control,
  fieldName = 'team_member.working_hours',
  getValues,
  setValue
}: {
  control: any;
  getValues: any;
  setValue: any;
  fieldName?: string;
}) {
  const memoizedColumns = useMemo(
    () => columns(control, fieldName, getValues, setValue),
    [control, fieldName, setValue, getValues]
  );
  return (
    <Box mt={4}>
      <Typography variant='h6' mb={2} fontSize={16}>
        Working Hours
      </Typography>
      <Box overflow='hidden'>
        <DataTable data={daysOfWeek} columns={memoizedColumns} isHeaderSticky={false} />
      </Box>
    </Box>
  );
}

const columns = (control: any, fieldName: string, getValues: any, setValue: any): ColumnDef<any>[] => [
  {
    header: 'DAY',
    accessorKey: 'label',
    cell: (info) => <Typography>{info.row.original.label}</Typography>
  },
  {
    header: 'STATUS',
    cell: (info) => <Switch name={`${fieldName}.${info.row.original.value}.open`} control={control} />
  },
  {
    header: 'TIME',
    cell: (info) => (
      <Box display='flex' gap={3}>
        <TimePicker name={`${fieldName}.${info.row.original.value}.from_time`} control={control} />
        <TimePicker name={`${fieldName}.${info.row.original.value}.to_time`} control={control} />
      </Box>
    )
  },
  {
    header: 'BREAK',
    cell: (info) => <BreaksCell control={control} fieldName={fieldName} dayKey={info.row.original.value} />
  },
  {
    header: 'ACTION',
    cell: (info) => (
      <CopyAction
        control={control}
        fieldName={fieldName}
        dayKey={info.row.original.value}
        getValues={getValues}
        setValue={setValue}
      />
    )
  }
];

const daysOfWeek = [
  { label: 'Monday', value: 'monday' },
  { label: 'Tuesday', value: 'tuesday' },
  { label: 'Wednesday', value: 'wednesday' },
  { label: 'Thursday', value: 'thursday' },
  { label: 'Friday', value: 'friday' },
  { label: 'Saturday', value: 'saturday' },
  { label: 'Sunday', value: 'sunday' }
];

function BreaksCell({ dayKey, control, fieldName }: { dayKey: string; control: any; fieldName: string }) {
  const fieldArrayName = `${fieldName}.${dayKey}.breaks`;

  const { fields, append, remove } = useFieldArray({
    control,
    name: fieldArrayName
  });

  return (
    <Box display='flex' flexDirection='column' alignItems='center'>
      {fields.map((f, idx) => (
        <Stack direction='row' spacing={1} key={f.id} mb={1.5}>
          <TimePicker name={`${fieldArrayName}.${idx}.from`} control={control} />
          <TimePicker name={`${fieldArrayName}.${idx}.to`} control={control} />
          <IconButton size='small' onClick={() => remove(idx)}>
            <RiDeleteBin6Line size={20} color='#DA0808' />
          </IconButton>
        </Stack>
      ))}

      <Button
        variant='outlined'
        size='small'
        onClick={() => append({ from: '', to: '' })}
        sx={{
          borderRadius: '999px',
          textTransform: 'none',
          mt: 1,
          px: 2,
          py: 0.5,
          fontSize: 13,
          borderColor: '#E0E0E0',
          color: '#333',
          backgroundColor: '#F9F9F9',
          '&:hover': {
            backgroundColor: '#F1F1F1',
            borderColor: '#D6D6D6'
          }
        }}
      >
        Add Breaks
      </Button>
    </Box>
  );
}

function CopyAction({
  dayKey,
  fieldName,
  getValues,
  setValue
}: {
  dayKey: string;
  control: any;
  fieldName: string;
  getValues: any;
  setValue: any;
}) {
  const handleCopy = () => {
    const source = getValues(`${fieldName}.${dayKey}`);

    daysOfWeek.forEach((day) => {
      const targetPath = `${fieldName}.${day.value}`;

      // ✅ copy simple fields
      setValue(`${targetPath}.open`, source?.open);
      setValue(`${targetPath}.from_time`, source?.from_time);
      setValue(`${targetPath}.to_time`, source?.to_time);

      // ✅ IMPORTANT: copy breaks properly
      const breaks = source?.breaks || [];

      setValue(
        `${targetPath}.breaks`,
        breaks.map((b: any) => ({
          from: b.from,
          to: b.to
        }))
      );
    });
  };

  return (
    <IconButton onClick={handleCopy}>
      <MdOutlineContentCopy size={20} />
    </IconButton>
  );
}
